// #region Vue
import {ref} from 'vue';
// #endregion

// #region Composables
import {useCssVar} from '@vueuse/core';
// #endregion

// #region Enums
import {modeEnum} from '@/enum/config.enum';
// #endregion

// #region Params, Ref, Reactives, Computed
const typePortal = ref(STTEDRA_TYPE_PORTAL);
// #endregion

/**
 * Composable to apply a dynamic theme by setting CSS custom properties on a root element.
 *
 * The theme colors are chosen based on `typePortal.value`, and automatically re-applied whenever
 * `typePortal` changes or when the root element becomes available.
 *
 * Usage:
 * <template>
 *   <div ref="rootEl">…</div>
 * </template>
 *
 * <script setup>
 *   import { useThemeChange } from '@/composables/useThemeChange';
 *   const { rootEl, applyTheme, typePortal } = useThemeChange();
 *   // `applyTheme` will run automatically, but can be called manually if needed
 * </script>
 *
 *
 * @returns {{
 *   rootEl: import('vue').Ref<HTMLElement|null>,
 *   applyTheme: () => void,
 *   typePortal: import('vue').Ref<string>
 * }}
 *   - rootEl: Template ref to the element whose CSS vars will be set.
 *   - applyTheme(): Manually re-apply the current theme.
 *   - typePortal: Reactive ref controlling which color scheme to use.
 */
export function useThemeChange() {
  // Template ref for the root element
  const rootEl = ref(null);

  const primaryColor = ref(typePortal.value === modeEnum.ABONO_ANUAL ? '#2c3e50' : '#ff8a69');
  const stteperOffColor = ref(typePortal.value === modeEnum.ABONO_ANUAL ? '#ccc' : '#ccc');

  /**
   * Computes the current color palette based on `typePortal.value`.
   *
   * @returns {Record<string, string>} An object mapping CSS variable names to their color values.
   */
  const computePalette = () => {
    const isAbonoAnual = typePortal.value === modeEnum.ABONO_ANUAL;
    return {
      '--border-color': isAbonoAnual ? '#8c9cae' : '#ffa08c',
      '--primary-color': isAbonoAnual ? '#2c3e50' : '#ff8a69',
      '--focus-outline': isAbonoAnual ? '#1d1d1b' : '#1d1d1b',
      '--focus-ring': isAbonoAnual ? '#2c3e50' : '#ff8a69',
      '--highlight-text-color': isAbonoAnual ? '#2c3e50' : '#ff8a69',
      '--primary-100': isAbonoAnual ? '#d9dee3' : '#ffe0d7',
      '--primary-200': isAbonoAnual ? '#b3bdc9' : '#ffc0b1',
      '--primary-300': isAbonoAnual ? '#8c9cae' : '#ffa08c',
      '--primary-400': isAbonoAnual ? '#667b94' : '#ff8f77',
      '--primary-500': isAbonoAnual ? '#2c3e50' : '#ff8a69',
      '--primary-600': isAbonoAnual ? '#243342' : '#e6785e',
      '--primary-700': isAbonoAnual ? '#1d2834' : '#bf5f4c',
      '--primary-800': isAbonoAnual ? '#151d25' : '#D7765B',
      '--primary-900': isAbonoAnual ? '#0e1217' : '#732f28',
      '$pv__url--chevron': isAbonoAnual ? '~@/assets/images/codigos/chevron.svg' : '~@/assets/images/verano-joven/chevron.svg',
    };
  };

  /**
   * Applies the current theme palette to the `rootEl`'s CSS variables.
   *
   * @returns {void}
   */
  function applyTheme() {
    const palette = computePalette();
    for (const [varName, color] of Object.entries(palette)) {
      const cssVarRef = useCssVar(varName, rootEl);
      cssVarRef.value = color;
    }
  }

  return {
    rootEl,
    applyTheme,
    typePortal,
    primaryColor,
    stteperOffColor,
  };
}
